import { useTranslation } from 'react-i18next'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '../button'
import { Select } from '../select'

export interface DataTablePaginationProps {
  pageIndex: number
  pageSize: number
  pageCount: number
  totalRowCount: number
  pageSizeOptions: readonly number[]

  onPageIndexChange: (pageIndex: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export function DataTablePagination({
  pageIndex,
  pageSize,
  pageCount,
  totalRowCount,
  pageSizeOptions,
  onPageIndexChange,
  onPageSizeChange,
}: DataTablePaginationProps) {
  const { t } = useTranslation()

  const from =
    totalRowCount === 0 ? 0 : pageIndex * pageSize + 1

  const to = Math.min(
    totalRowCount,
    (pageIndex + 1) * pageSize,
  )

  const pageSizeSelectOptions = pageSizeOptions.map(
    (option) => ({
      id: String(option),
      label: String(option),
    }),
  )

  return (
    <div
      className={[
        'flex flex-col gap-3',
        'rounded-lg border border-(--color-border)',
        'bg-(--color-surface-subtle) px-3 py-3',
        'lg:flex-row lg:items-center',
        'lg:justify-between',
      ].join(' ')}
    >
      <p
        aria-live="polite"
        aria-atomic="true"
        className="text-xs font-medium text-(--color-muted-foreground)"
      >
        {t('ui.dataTable.pagination.summary', {
          from,
          to,
          total: totalRowCount,
        })}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          label={
            <span className="sr-only">
              {t('ui.dataTable.pagination.pageSize')}
            </span>
          }
          options={pageSizeSelectOptions}
          value={String(pageSize)}
          onChange={(value) => {
            if (value !== null) {
              onPageSizeChange(Number(value))
            }
          }}
          className="w-24 gap-0"
        />

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="small"
            isDisabled={pageIndex <= 0}
            aria-label={t('ui.dataTable.pagination.previous')}
            className="w-9 px-0 sm:w-auto sm:px-3"
            onPress={() => {
              onPageIndexChange(pageIndex - 1)
            }}
          >
            <ChevronLeft
              aria-hidden="true"
              className="h-4 w-4"
            />

            <span className="hidden sm:inline">
              {t('ui.dataTable.pagination.previous')}
            </span>
          </Button>

          <span className="min-w-24 px-1 text-center text-sm font-semibold tabular-nums whitespace-nowrap">
            {t('ui.dataTable.pagination.page', {
              current: pageIndex + 1,
              total: pageCount,
            })}
          </span>

          <Button
            variant="secondary"
            size="small"
            isDisabled={pageIndex >= pageCount - 1}
            aria-label={t('ui.dataTable.pagination.next')}
            className="w-9 px-0 sm:w-auto sm:px-3"
            onPress={() => {
              onPageIndexChange(pageIndex + 1)
            }}
          >
            <span className="hidden sm:inline">
              {t('ui.dataTable.pagination.next')}
            </span>

            <ChevronRight
              aria-hidden="true"
              className="h-4 w-4"
            />
          </Button>
        </div>
      </div>
    </div>
  )
}
